/**
 * Per-agent whitelist settings for the CRM opportunity tools.
 *
 * The two tools `crm_update_opportunity_fields` and
 * `crm_move_opportunity_stage` gate what the LLM is allowed to write
 * via a per-agent whitelist stored in the agent config column:
 *   - `email_agents.config.crm_tools.settings[toolName]`
 *   - `message_agents.config.crm_tools.settings[toolName]`
 *
 * This module is the typed contract between the storage layer
 * (operator writes via the CRM tool config dialog) and the tool
 * registration layer (prefetch reads to decide whether to register
 * the tool and which fields / stages to expose in the JSON Schema).
 *
 * Design rules:
 *
 *   1. **Closed union of tool names.** Both read paths go through the
 *      `CRM_OPPORTUNITY_TOOL_NAME` constants — typos get caught at
 *      compile time.
 *
 *   2. **Fail-safe.** The read helper returns `null` for any condition
 *      that would leave the tool without a usable whitelist:
 *        - tool name not in `enabled[]` (master toggle off),
 *        - settings entry missing or not an object,
 *        - zod validation fails (including empty `allowed_*_codes`),
 *        - whitelist exceeds the hard cap.
 *      Returning `null` instead of throwing lets the Phase 5 tool
 *      creator cleanly decline to register — no crash, no empty JSON
 *      Schema being shown to the LLM.
 *
 *   3. **Caps.** `CRM_FIELD_CODES_SOFT_CAP = 40` / `HARD_CAP = 100`.
 *      Hard cap is enforced via zod. Soft cap is exported for the UI
 *      layer to render an advisory when the operator saves a whitelist
 *      above it; the backend does NOT block on the soft cap. The
 *      numbers are order-of-magnitude derived from a typical select
 *      field adding ~100-150 tokens to the JSON Schema (label +
 *      description + enum list + overhead) — 100 fields ≈ 10-15k tokens
 *      per agent turn just for one tool's schema. Phase 7.1 telemetry
 *      will let us revise these once we have production numbers.
 *
 *   4. **No `as any`.** Generics + a conditional type give callers a
 *      type-safe dispatch: `validateCrmOpportunityToolSettings(raw,
 *      'crm_update_opportunity_fields')` returns
 *      `CrmUpdateFieldsSettings | null` with no casts.
 */

import { z } from 'zod';

// =====================================================
// Tool name constants (closed union)
// =====================================================

export const CRM_OPPORTUNITY_TOOL_NAME = {
  updateFields: 'crm_update_opportunity_fields',
  moveStage: 'crm_move_opportunity_stage',
} as const;

export type CrmOpportunityToolName =
  | typeof CRM_OPPORTUNITY_TOOL_NAME.updateFields
  | typeof CRM_OPPORTUNITY_TOOL_NAME.moveStage;

// =====================================================
// Caps (shared with UI)
// =====================================================

/**
 * Soft warning threshold for `allowed_field_codes`. UI shows an advisory
 * when the operator saves a whitelist at or above this size; save still
 * proceeds. The backend does NOT enforce this — only the hard cap.
 */
export const CRM_FIELD_CODES_SOFT_CAP = 40;

/**
 * Hard cap for `allowed_field_codes`. Zod rejects whitelists above this
 * size and the operator gets a clear validation error.
 */
export const CRM_FIELD_CODES_HARD_CAP = 100;

// =====================================================
// Schemas
// =====================================================

/**
 * Settings shape for `crm_update_opportunity_fields`.
 *
 *   - `allowed_field_codes`: list of `CrmFieldSchema.code` slugs the
 *     agent is permitted to write. Must contain at least one entry —
 *     an empty list means "no fields allowed", in which case the tool
 *     should not register at all (Phase 4.2 fail-safe). System fields
 *     (`name`, `price`) are listed by their literal keys when the
 *     operator wants the agent to write them; they are NOT schema-
 *     derived.
 *
 * Operator-authored instructions are NOT part of this schema — they
 * follow the standard tool-instruction path (`ai_tools.system_instructions`
 * default, `tenant_ai_tool_instructions` per-tenant + scoped overrides,
 * `applyStoredInstructions` at runtime). The shared
 * `ToolInstructionsDialog` UI edits them.
 */
export const crmUpdateFieldsSettingsSchema = z
  .object({
    allowed_field_codes: z
      .array(z.string().min(1))
      .min(1)
      .max(CRM_FIELD_CODES_HARD_CAP),
  })
  .strict();

export type CrmUpdateFieldsSettings = z.infer<typeof crmUpdateFieldsSettingsSchema>;

/**
 * Settings shape for `crm_move_opportunity_stage`.
 *
 *   - `allowed_stage_codes`: list of `CrmStage.id` values (stringified)
 *     the agent is permitted to move opportunities into. Must contain
 *     at least one entry.
 *
 * Pipeline disambiguation is handled at tool-schema level: the LLM
 * always receives `stage` + `pipeline` as required parameters, so
 * operators no longer need a separate pipeline whitelist here.
 *
 * Operator-authored instructions are NOT part of this schema — see
 * the equivalent note on `crmUpdateFieldsSettingsSchema`.
 */
export const crmMoveStageSettingsSchema = z
  .object({
    allowed_stage_codes: z.array(z.string().min(1)).min(1),
  })
  .strict();

export type CrmMoveStageSettings = z.infer<typeof crmMoveStageSettingsSchema>;

// =====================================================
// Typed read helper
// =====================================================

/**
 * Conditional type: picks the right settings type for the given tool
 * name. Lets the read helper return a precisely-typed object without
 * any `as` casts at the call site.
 */
export type CrmOpportunityToolSettings<T extends CrmOpportunityToolName> =
  T extends typeof CRM_OPPORTUNITY_TOOL_NAME.updateFields
    ? CrmUpdateFieldsSettings
    : T extends typeof CRM_OPPORTUNITY_TOOL_NAME.moveStage
    ? CrmMoveStageSettings
    : never;

/**
 * Validate a **raw settings object** for one of the two CRM opportunity
 * tools. Pure zod-validation layer used by:
 *
 *   - `prefetchCrmToolContext` (resolver prefetch step)
 *   - CRM settings save routes (backend validation before write)
 *   - CRM refresh-schema drift computation
 *
 * Returns `null` when the raw entry is missing, non-object, or fails
 * zod validation. Never throws — validation failures emit a warn-log
 * with the tool name and the zod error summary.
 */
export function validateCrmOpportunityToolSettings<T extends CrmOpportunityToolName>(
  raw: unknown,
  toolName: T
): CrmOpportunityToolSettings<T> | null {
  if (!raw || typeof raw !== 'object') {
    console.warn(
      `[CrmToolSettings] tool "${toolName}" settings entry missing or not an object — tool will not register`
    );
    return null;
  }

  const schema =
    toolName === CRM_OPPORTUNITY_TOOL_NAME.updateFields
      ? crmUpdateFieldsSettingsSchema
      : crmMoveStageSettingsSchema;

  const result = schema.safeParse(raw);
  if (!result.success) {
    console.warn(
      `[CrmToolSettings] tool "${toolName}" settings failed validation — tool will not register`,
      result.error.flatten()
    );
    return null;
  }

  // Internal cast, sound but TS can't see it through the ternary dispatch
  // above: both branches produce exactly `CrmOpportunityToolSettings<T>`
  // at runtime (the schema was picked from the same `toolName` that narrows
  // `T`), but the conditional return type cannot be inferred from a
  // non-discriminated ternary.
  return result.data as CrmOpportunityToolSettings<T>;
}

